import React from 'react';
import { useTranslation } from 'react-i18next';
import lightingImg from '../Products/img/products-lighting-content.webp';
import './css/products.css';
import { ensureNamespace } from '../../i18n/ensurePageI18n';

const IMAGE_SRC = lightingImg;

const loadI18n = (i18n) => {
  ensureNamespace(i18n, 'lighting', {
    en: {
      sectionTitle: 'Surface Lighting',
      sectionSubtitle: 'Compact • Energy-Efficient • Optimized for Web Inspection',
      title: 'SURFACE LIGHTING',
      intro1: 'Compact, energy-efficient linear light sources engineered for continuous 24/7 web inspection environments.',
      intro2: 'Proper illumination is critical for effective flaw detection and must account for material texture, web speed, defect size and type, color, and ambient conditions.',
      intro3: 'Wintriss holds numerous patents for lighting optimized for surface inspection and excels at determining the most effective solution for highly specific applications.',
      intro4: 'Enhanced image clarity produced with these techniques yields superior detection and classification, exposing extremely small defects undetectable by traditional systems.',
      featuresTitle: 'Key Capabilities',
      highIntensityTitle: 'High Intensity',
      highIntensityDesc: 'Up to 100,000 lux for demanding high-speed inspection.',
      reliabilityTitle: 'Reliability',
      reliabilityDesc: 'Rated up to 100,000 operating hours for 24/7 production.',
      modularWidthTitle: 'Modular Width',
      modularWidthDesc: 'Scalable design adapts to any web width requirement.',
      uniformityTitle: 'Uniformity',
      uniformityDesc: 'Outstanding illumination uniformity for consistent detection.',
      hpSodiumVaporTitle: 'HP Sodium Vapor',
      hpSodiumVaporDesc: 'High-intensity front lighting for reflective surfaces.',
      refractionRangerTitle: 'Refraction Ranger (LED)',
      refractionRangerDesc: 'Collimated LED illumination for translucent materials.',
      modifiedSchlierenTitle: 'Modified Schlieren',
      modifiedSchlierenDesc: 'Optimized for plastic film and glass defect enhancement.',
      biLevelLightingTitle: 'Bi-Level Lighting',
      biLevelLightingDesc: 'Dual-plane lighting highlights 3D defects in metal, film, glass.',
      ledRangerTitle: 'LED Ranger (LED)',
      ledRangerDesc: 'Front or back light in multiple wavelengths/intensities, diffused or focused.',
      viewFull: 'View fullsize'
    },
    zh: {
      sectionTitle: '光源',
      sectionSubtitle: '精巧・節能・針對卷料檢測最佳化',
      title: 'SURFACE LIGHTING',
      intro1: '精巧且節能的線性光源，專為 24/7 連續卷料檢測環境而設計。',
      intro2: '正確的照明技術對瑕疵偵測至關重要，須考量材質紋理、卷料速度、缺陷尺寸與類型、顏色及環境光。',
      intro3: 'Wintriss 擁有多項針對表面檢測最佳化的照明專利，擅長為高度特定應用選出最有效方案。',
      intro4: '以這些技術取得的影像清晰度提升偵測與分類品質，能顯現傳統系統無法察覺的極微小缺陷。',
      featuresTitle: '核心能力',
      highIntensityTitle: '超高光強',
      highIntensityDesc: '光強可達 100,000 lux，滿足高速檢測需求。',
      reliabilityTitle: '高可靠度',
      reliabilityDesc: '額定壽命最高可達 100,000 小時，適合 24/7 生產。',
      modularWidthTitle: '寬度模組化',
      modularWidthDesc: '可依任意卷料寬度彈性擴充配置。',
      uniformityTitle: '高均勻性',
      uniformityDesc: '卓越均勻度確保偵測結果穩定一致。',
      hpSodiumVaporTitle: '高壓鈉燈',
      hpSodiumVaporDesc: '高強度前向照明，適合反射性材質。',
      refractionRangerTitle: 'Refraction Ranger (LED)',
      refractionRangerDesc: '平行光（準直）LED，適用半透或透光材質。',
      modifiedSchlierenTitle: '改良 Schlieren',
      modifiedSchlierenDesc: '強化塑膠薄膜與玻璃細微缺陷呈現。',
      biLevelLightingTitle: '雙層照明',
      biLevelLightingDesc: '雙平面光源凸顯金屬箔、薄膜、玻璃的 3D 缺陷。',
      ledRangerTitle: 'LED Ranger (LED)',
      ledRangerDesc: '多波長/強度前或背光，支援漫射或聚焦配置。',
      viewFull: '查看原尺寸'
    }
  });
};

const Lighting = () => {
  const { t, i18n } = useTranslation();
  loadI18n(i18n); // synchronous load

  const features = [
    { k: 'highIntensity' },
    { k: 'reliability' },
    { k: 'modularWidth' },
    { k: 'uniformity' },
    { k: 'hpSodiumVapor' },
    { k: 'refractionRanger' },
    { k: 'modifiedSchlieren' },
    { k: 'biLevelLighting' },
    { k: 'ledRanger' }
  ];

  return (
    <div>
      <section className="lighting-section relative text-white py-20">
        <div className="absolute inset-0 bg-black bg-opacity-70" />
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-5xl font-bold mb-4">{t('lighting:sectionTitle')}</h1>
          <p className="text-xl text-white">{t('lighting:sectionSubtitle')}</p>
        </div>
      </section>

      <main
        style={{
          padding: 'clamp(1rem,2vw,2.5rem)',
          maxWidth: '1200px',
          margin: '0 auto',
          fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
          lineHeight: 1.6
        }}
      >
        <header style={{ marginBottom: '1.5rem' }}>
          <h2 style={{
            fontSize: 'clamp(1.8rem,4vw,2.6rem)',
            margin: 0,
            letterSpacing: '.5px'
          }}>
            {t('lighting:title')}
          </h2>
        </header>

        <section
          style={{
            display: 'grid',
            gap: '2rem',
            gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%, 300px),1fr))',
            alignItems: 'start',
            marginBottom: '2.5rem'
          }}
        >
          <article>
            <p>{t('lighting:intro1')}</p>
            <p>{t('lighting:intro2')}</p>
            <p>{t('lighting:intro3')}</p>
            <p>{t('lighting:intro4')}</p>
          </article>
          <figure style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
            <div
              style={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(0,0,0,.12)'
              }}
            >
              <img
                src={IMAGE_SRC}
                alt={t('lighting:sectionTitle')}
                style={{ width: '100%', height: 'auto', display: 'block', objectFit: 'cover' }}
                loading="lazy"
              />
            </div>
          </figure>
        </section>

        <section style={{ marginBottom: '3rem' }}>
          <h3 style={{ fontSize: '1.25rem', margin: '0 0 1.25rem', fontWeight: 600 }}>
            {t('lighting:featuresTitle')}
          </h3>
          <div
            style={{
              display: 'grid',
              gap: '1.1rem',
              gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))'
            }}
            aria-label="Lighting capabilities"
          >
            {features.map(f => (
              <div
                key={f.k}
                style={{
                  padding: '1rem',
                  background: '#f8f9fa',
                  border: '1px solid #e2e6ea',
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '.5rem'
                }}
              >
                <strong style={{ fontSize: '.9rem' }}>
                  {t(`lighting:${f.k}Title`)}
                </strong>
                <p style={{ fontSize: '.7rem', color: '#555', margin: 0 }}>
                  {t(`lighting:${f.k}Desc`)}
                </p>
                <span style={{
                  fontSize: '.6rem',
                  letterSpacing: '.5px',
                  color: '#888',
                  textTransform: 'uppercase'
                }}>
                  {f.k}
                </span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Lighting;

